import {
    formatDate as stormFormatDate,
    formatTime as stormFormatTime,
} from "@amzn/storm-ui-utils";

import { FALLBACK_VALUE } from "../constants/common";

export const isValidDateString = (dateString: string): boolean => {
    return !isNaN(Date.parse(dateString));
};

/**
 * Defines a date format that can be passed into various functions to help properly format dates
 * Now maps to Storm's dateStyle options
 */
export enum DateFormat {
    ISO_8601_CALENDAR_DATE_FORMAT = "short",
    LONG_DATE_FORMAT = "medium",
}

/**
 * Defines a IANA timezone that can be passed to format in specific timezone
 */
export enum Timezone {
    EST = "America/New_York",
    CST = "America/Chicago",
    MST = "America/Denver",
    PST = "America/Los_Angeles",
    UTC = "UTC",
    GMT = "GMT",
    JST = "Asia/Tokyo",
    IST = "Asia/Kolkata",
    CET = "Europe/Paris",
}

/**
 * Formats the given date using Storm's international formatting utilities
 * @param toFormat The date we would like to format
 * @param dateFormat The format style ('short' | 'medium'). Defaults to 'short'
 * @param formatTimezone The timezone you would like to format it into
 * @param locale The locale to use for formatting. If not provided, uses browser's default locale
 * @returns A UI friendly string we can display
 */
export const formatDate = (
    toFormat: Date | string | undefined,
    dateFormat: DateFormat = DateFormat.ISO_8601_CALENDAR_DATE_FORMAT,
    formatTimezone?: string,
    locale?: string,
): string => {
    try {
        if (!toFormat) {
            return FALLBACK_VALUE.INVALID_DATE_LABEL;
        }

        const date = new Date(toFormat);
        if (isNaN(date.getTime())) {
            return FALLBACK_VALUE.INVALID_DATE_LABEL;
        }

        return stormFormatDate({
            date,
            locale: locale || navigator.language,
            dateStyle: dateFormat as "short" | "medium",
            timezone: formatTimezone,
        });
    } catch (error) {
        console.error(
            `Error formatting date ${String(toFormat)} in format ${dateFormat}`,
            error,
        );
        return toFormat ? String(toFormat) : FALLBACK_VALUE.INVALID_DATE_LABEL;
    }
};

/**
 * Formats date and time using Storm's international formatting utilities
 * Output format: "12/2/25 9:30 AM EST" (for en-US locale)
 * @param date The date to format
 * @param timezone Optional timezone for formatting
 * @param locale Optional locale. If not provided, uses browser's default locale
 * @returns A formatted date and time string
 */
export const formatDateTime = (
    date: string | undefined | null,
    timezone?: string,
    locale?: string,
): string => {
    if (date === undefined || date === null) {
        return "date undefined";
    }
    try {
        const parsedDate = new Date(date);
        if (isNaN(parsedDate.getTime())) {
            return "date cannot be parsed";
        }

        const browserLocale = locale || navigator.language;

        const formattedDate = stormFormatDate({
            date: parsedDate,
            locale: browserLocale,
            dateStyle: "short",
        });

        const formattedTime = stormFormatTime({
            date: parsedDate,
            locale: browserLocale,
            timezone,
        });

        return `${formattedDate} ${formattedTime}`;
    } catch {
        return "date cannot be parsed";
    }
};

/**
 * A helper function to generate a sort function that can be passed to [].sort(). e.g. if you have an object with a parsable date field
 * interface dateObject { dateField: Date | string | number }, you can sort it by calling dateObject[].sort(dateSort("dateField")).
 * @param key The key for the date parsable field we want to sort upon
 * @param isAscending Should we sort the dates in ascending order, e.g. 1/1, 1/2, 1/3. Default to false / a descending list
 * @returns A function that can be passed to the generic collections sort method
 */
export function dateSort<
    T extends Partial<Record<K, string | number | Date>>,
    K extends keyof T,
>(key: K, isAscending: boolean = false): (a: T, b: T) => number {
    return (a: T, b: T) => {
        const dateA = a[key] ? new Date(a[key]!) : new Date();
        const dateB = b[key] ? new Date(b[key]!) : new Date();
        return (dateA.getTime() - dateB.getTime()) * (isAscending ? 1 : -1);
    };
}
